import React, {useState} from 'react';
import {View, Text} from 'react-native';

interface ReadMoreProps {
  numberOfLines?: number;
  renderTruncatedFooter?: (handlePress: () => void) => React.ReactNode;
  renderRevealedFooter?: (handlePress: () => void) => React.ReactNode;
  children?: React.ReactNode;
}

/**
 * Web-compatible ReadMore component
 * Provides collapsible text functionality similar to react-native-read-more
 */
const ReadMore: React.FC<ReadMoreProps> = ({
  numberOfLines = 3,
  renderTruncatedFooter,
  renderRevealedFooter,
  children,
}) => {
  const [showFullText, setShowFullText] = useState(false);

  const toggleShowMore = () => {
    setShowFullText(!showFullText);
  };

  // Extract text from children to count lines
  const childrenText = React.Children.toArray(children)
    .map((child) => {
      if (typeof child === 'string') return child;
      if (React.isValidElement(child) && child.props.children) {
        return child.props.children;
      }
      return '';
    })
    .join('');

  // Simple line counting approximation (counts newlines and word wraps)
  const lines = childrenText.split('\n');
  const shouldTruncate =
    numberOfLines && lines.length > numberOfLines && !showFullText;

  return (
    <View>
      {showFullText ? (
        <>
          {children}
          {renderRevealedFooter && (
            <View>{renderRevealedFooter(toggleShowMore)}</View>
          )}
        </>
      ) : (
        <>
          {shouldTruncate ? (
            <>
              {React.Children.map(children, (child) => {
                if (React.isValidElement(child) && child.type === Text) {
                  return React.cloneElement(child, {
                    numberOfLines,
                  });
                }
                return child;
              })}
              {renderTruncatedFooter && (
                <View>{renderTruncatedFooter(toggleShowMore)}</View>
              )}
            </>
          ) : (
            <>
              {children}
              {renderRevealedFooter && (
                <View>{renderRevealedFooter(toggleShowMore)}</View>
              )}
            </>
          )}
        </>
      )}
    </View>
  );
};

export default ReadMore;
